import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from "@tanstack/react-query";
import { useApiClient } from "@shell/apiClientContext";
import { FLOORPLAN_QUERY_KEY } from "../common/queryKeys";
import { WORKSPACES_QUERY_KEY } from "../workspaces/hooks";
import {
    createWorkspaceAssignment,
    releaseWorkspaceAssignment,
    searchWorkspaceAssignments,
} from "./api";
import type {
    WorkspaceAssignmentCreatePayload,
    WorkspaceAssignmentMutationResult,
    WorkspaceAssignmentView,
} from "./types";

export const WORKSPACE_ASSIGNMENTS_QUERY_KEY = ["admin", "workspace-assignments"] as const;

export interface SearchWorkspaceAssignmentsParams {
    readonly workspaceId: string;
    readonly userId: string;
    readonly openOnly: boolean;
}

export function useWorkspaceAssignments(
    params: SearchWorkspaceAssignmentsParams,
): UseQueryResult<readonly WorkspaceAssignmentView[], Error> {
    const client = useApiClient();
    return useQuery({
        queryKey: [
            ...WORKSPACE_ASSIGNMENTS_QUERY_KEY,
            params.workspaceId,
            params.userId,
            params.openOnly,
        ],
        queryFn: () =>
            searchWorkspaceAssignments(client, {
                workspaceId: params.workspaceId,
                userId: params.userId,
                ...(params.openOnly ? { open: true } : {}),
            }),
        staleTime: 30_000,
    });
}

export function useCreateWorkspaceAssignment(): UseMutationResult<
    WorkspaceAssignmentMutationResult,
    Error,
    WorkspaceAssignmentCreatePayload
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => createWorkspaceAssignment(client, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: WORKSPACE_ASSIGNMENTS_QUERY_KEY });
            // Workspace.currentAssignmentId changes; refresh the workspace list.
            void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}

export interface ReleaseWorkspaceAssignmentInput {
    readonly workspaceId: string;
    readonly assignmentId: string;
}

export function useReleaseWorkspaceAssignment(): UseMutationResult<
    WorkspaceAssignmentView,
    Error,
    ReleaseWorkspaceAssignmentInput
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ workspaceId, assignmentId }) =>
            releaseWorkspaceAssignment(client, workspaceId, assignmentId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: WORKSPACE_ASSIGNMENTS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}
